<section class="product-card-analytics-page">
  <header class="analytics-header">
    <div>
      <p class="eyebrow">Shine AI reporting</p>
      <h1>Product-card analytics</h1>
      <p class="header-copy">
        Aggregated product-card display, view, hover, and click metrics from the reporting API.
      </p>
    </div>

    <div class="analytics-actions">
      <mat-button-toggle-group
        [ngModel]="selectedRange"
        (ngModelChange)="applyQuickRange($event)"
        aria-label="Date range">
        @for (range of ranges; track range.value) {
          <mat-button-toggle [value]="range.value">{{ range.label }}</mat-button-toggle>
        }
      </mat-button-toggle-group>

      <mat-button-toggle-group
        [ngModel]="selectedGrain"
        (ngModelChange)="selectedGrain = $event"
        aria-label="Report grain">
        @for (grain of grains; track grain.value) {
          <mat-button-toggle [value]="grain.value">{{ grain.label }}</mat-button-toggle>
        }
      </mat-button-toggle-group>
    </div>
  </header>

  <section class="filter-panel" aria-label="Product-card analytics filters">
    <mat-form-field appearance="outline">
      <mat-label>Start</mat-label>
      <input matInput type="date" [(ngModel)]="startDate" (ngModelChange)="selectedRange = 'custom'">
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>End</mat-label>
      <input matInput type="date" [(ngModel)]="endDate" (ngModelChange)="selectedRange = 'custom'">
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>Chatbot</mat-label>
      <input matInput [(ngModel)]="chatbotId" type="search" placeholder="Exact chatbot id">
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>Assistant</mat-label>
      <input matInput [(ngModel)]="assistantId" type="search" placeholder="Exact assistant id">
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>Card type</mat-label>
      <mat-select [(ngModel)]="productCardType">
        @for (option of cardTypeOptions; track option.value) {
          <mat-option [value]="option.value">{{ option.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>Presentation</mat-label>
      <mat-select [(ngModel)]="presentation">
        @for (option of presentationOptions; track option.value) {
          <mat-option [value]="option.value">{{ option.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>SKU/name search</mat-label>
      <input matInput [(ngModel)]="productSearch" type="search" placeholder="Exact SKU, name, or URL">
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>Source</mat-label>
      <mat-select [(ngModel)]="eventSource">
        @for (option of sourceOptions; track option.value) {
          <mat-option [value]="option.value">{{ option.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <div class="filter-buttons">
      <button mat-flat-button color="primary" type="button" (click)="applyFilters()" [disabled]="loading || !startDate || !endDate">
        Apply
      </button>
      <button mat-button type="button" (click)="clearFilters()" [disabled]="loading">
        Clear
      </button>
    </div>
  </section>

  @if (errorMessage) {
    <div class="analytics-alert" role="alert">{{ errorMessage }}</div>
  }

  @if (loading) {
    <div class="analytics-loading" role="status">
      <mat-spinner diameter="40"></mat-spinner>
      <span>Loading product-card analytics</span>
    </div>
  } @else if (report && !hasData()) {
    <section class="analytics-empty">
      <strong>No product-card activity found</strong>
      <span>Adjust the date range or filters to include aggregate documents.</span>
    </section>
  } @else if (report) {
    <section class="kpi-groups" aria-label="Product-card KPIs">
      @for (row of kpiRows; track trackKpiRow($index, row)) {
        <section class="kpi-row" [attr.aria-label]="row.title + ' KPIs'">
          <h2>{{ row.title }}</h2>
          <div class="kpi-grid">
            @for (kpi of row.kpis; track trackKpi($index, kpi)) {
              <article class="kpi-tile">
                <span>{{ kpi.label }}</span>
                <strong>{{ kpi.value }}</strong>
                <small>{{ kpi.hint }}</small>
              </article>
            }
          </div>
        </section>
      }
    </section>

    <section class="chart-panel">
      <header>
        <div>
          <h2>Trend by period</h2>
          <span>{{ report.startDate }} to {{ report.endDate }}</span>
        </div>
        <span class="filter-summary">
          {{ selectedGrain }} grain
        </span>
      </header>
      <apx-chart
        [series]="trendSeries"
        [chart]="trendChart"
        [xaxis]="trendXAxis"
        [yaxis]="trendYAxis"
        [stroke]="trendStroke"
        [dataLabels]="trendDataLabels"
        [legend]="trendLegend"
        [tooltip]="trendTooltip"
        [grid]="trendGrid">
      </apx-chart>
    </section>

    <section class="breakdown-grid" aria-label="Product-card breakdowns">
      @for (section of breakdowns; track trackBreakdown($index, section)) {
        <article class="breakdown-panel">
          <header>
            <h2>{{ section.title }}</h2>
            <span>Top {{ section.rows.length }}</span>
          </header>

          @if (section.rows.length) {
            <div class="breakdown-table-wrap">
              <table class="analytics-table breakdown-table">
                <thead>
                <tr>
                  <th scope="col">Dimension</th>
                  <th scope="col" class="align-right">Displayed</th>
                  <th scope="col" class="align-right">Viewed</th>
                  <th scope="col" class="align-right">Clicks</th>
                  <th scope="col" class="align-right">View rate</th>
                  <th scope="col" class="align-right">CTR</th>
                </tr>
                </thead>
                <tbody>
                  @for (row of section.rows; track trackBreakdownRow($index, row)) {
                    <tr>
                      <td class="dimension-cell" [title]="row.detail || row.label">
                        <span>{{ row.label }}</span>
                        @if (row.detail) {
                          <small>{{ row.detail }}</small>
                        }
                      </td>
                      <td class="align-right">{{ row.displayed | number }}</td>
                      <td class="align-right">{{ row.viewed | number }}</td>
                      <td class="align-right">{{ row.clicks | number }}</td>
                      <td class="align-right">{{ formatRate(row.viewRate) }}</td>
                      <td class="align-right">{{ formatRate(row.clickThroughRateByDisplay) }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          } @else {
            <div class="compact-empty">No breakdown rows.</div>
          }
        </article>
      }
    </section>

    <section class="product-table-panel">
      <header>
        <div>
          <h2>Products</h2>
          <span>SKU and name when available, with analytics identity fallback</span>
        </div>
        <mat-button-toggle-group
          [ngModel]="productSort"
          (ngModelChange)="applyProductSort($event)"
          aria-label="Product table sort">
          <mat-button-toggle value="clicks">Clicks</mat-button-toggle>
          <mat-button-toggle value="viewed">Viewed</mat-button-toggle>
        </mat-button-toggle-group>
      </header>

      <div class="table-wrap">
        @if (productRows.length) {
          <table class="analytics-table product-table">
            <thead>
            <tr>
              <th scope="col">SKU</th>
              <th scope="col">Name</th>
              <th scope="col" class="align-right">Displayed</th>
              <th scope="col" class="align-right">Viewed</th>
              <th scope="col" class="align-right">Hovered</th>
              <th scope="col" class="align-right">Clicks</th>
              <th scope="col" class="align-right">View rate</th>
              <th scope="col" class="align-right">CTR display</th>
              <th scope="col" class="align-right">CTR view</th>
            </tr>
            </thead>
            <tbody>
              @for (row of productRows; track trackProduct($index, row)) {
                <tr>
                  <td>
                    <span class="hash-label" [title]="row.skuLabel">{{ row.skuLabel }}</span>
                    @if (row.skuFallbackLabel !== row.skuLabel) {
                      <small [title]="row.skuFallbackLabel">{{ row.skuFallbackLabel }}</small>
                    } @else {
                      <small [title]="row.cardIdLabel">{{ row.cardIdLabel }}</small>
                    }
                  </td>
                  <td>
                    <span class="hash-label" [title]="row.nameLabel">{{ row.nameLabel }}</span>
                    @if (row.nameFallbackLabel !== row.nameLabel) {
                      <small [title]="row.nameFallbackLabel">{{ row.nameFallbackLabel }}</small>
                    } @else {
                      <small [title]="row.productKey">{{ row.productKey }}</small>
                    }
                  </td>
                  <td class="align-right">{{ row.displayed | number }}</td>
                  <td class="align-right">{{ row.viewed | number }}</td>
                  <td class="align-right">{{ row.hovered | number }}</td>
                  <td class="align-right">{{ row.clicks | number }}</td>
                  <td class="align-right">{{ formatRate(row.viewRate) }}</td>
                  <td class="align-right">{{ formatRate(row.clickThroughRateByDisplay) }}</td>
                  <td class="align-right">{{ formatRate(row.clickThroughRateByView) }}</td>
                </tr>
              }
            </tbody>
          </table>
        } @else {
          <div class="analytics-empty compact">
            <strong>No product rows</strong>
            <span>The report can have totals before product hashes are available in newer aggregates.</span>
          </div>
        }
      </div>
    </section>
  }
</section>
